"use client";

import "@/app/(public)/shared/tokens/colors.css";
import { fetchChangelogs } from "@/lib/publications/api";
import { ChangelogEntry } from "@/lib/publications/types";
import Link from "next/link";
import { useEffect, useState } from "react";
import { SparksSolid, CheckCircleSolid, Globe, Megaphone, Bug } from "iconoir-react";
import { ArrowLeft } from "lucide-react";

// Category colors and icons
const categoryConfig = {
  feature: {
    color: "bg-purple-100 border border-purple-300 text-purple-800",
    icon: SparksSolid,
    label: "New Feature",
  },
  improvement: {
    color: "bg-blue-100 border border-blue-300 text-blue-800",
    icon: CheckCircleSolid,
    label: "Improvement",
  },
  integration: {
    color: "bg-green-100 border border-green-300 text-green-800",
    icon: Globe,
    label: "Integration",
  },
  announcement: {
    color: "bg-orange-100 border border-orange-300 text-orange-800",
    icon: Megaphone,
    label: "Announcement",
  },
  bugfix: {
    color: "bg-red-100 border border-red-300 text-red-800",
    icon: Bug,
    label: "Bug Fix",
  },
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function ChangeLog() {
  const [changelogEntries, setChangelogEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch changelog entries on component mount
  useEffect(() => {
    const loadChangelogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const changelogs = await fetchChangelogs();
        setChangelogEntries(changelogs);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load changelog entries");
        console.error("Error fetching changelogs:", err);
      } finally {
        setLoading(false);
      }
    };

    loadChangelogs();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-landing-primary container-page">
        {/* Header Skeleton */}
        <div className="bg-white border-b">
          <div className="py-24 space-y-4">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="space-y-2">
              <div className="h-6 w-full max-w-2xl bg-gray-200 rounded animate-pulse" />
              <div className="h-6 w-3/4 max-w-xl bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Changelog Entries Skeleton */}
        <div className="py-12">
          <div className="space-y-12">
            {/* Entry 1 */}
            <article className="bg-white rounded-lg shadow-sm border p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse" />
                    <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-2/3 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse" />
                <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
                <div className="h-6 w-14 bg-gray-200 rounded-full animate-pulse" />
              </div>
            </article>

            {/* Entry 2 */}
            <article className="bg-white rounded-lg shadow-sm border p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-28 bg-gray-200 rounded-full animate-pulse" />
                    <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-8 w-4/5 bg-gray-200 rounded animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-3/4 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <div className="h-6 w-18 bg-gray-200 rounded-full animate-pulse" />
                <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse" />
              </div>
            </article>

            {/* Entry 3 */}
            <article className="bg-white rounded-lg shadow-sm border p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
                    <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="h-8 w-2/3 bg-gray-200 rounded animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-6 w-4/5 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen container-page" style={{ backgroundColor: "var(--landing-bg-primary-sand)" }}>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="text-lg text-red-600">Error: {error}</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const listedEntries = changelogEntries.filter((e) => !e.unlisted);

  // Sort entries by date (newest first)
  const sortedEntries = [...listedEntries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="min-h-screen bg-landing-primary container-page flex flex-col gap-12">
      {/* Header */}
      <div className="container-top">
        <div className="flex flex-col gap-4">
          <h1 className="text-h2 text-landing-primary">Changelog</h1>
          <p className="text-body-lg text-landing-secondary max-w-2xl">
            Stay up to date with the latest features, improvements, and updates to the Nowadays platform.
          </p>
        </div>
      </div>

      <div className="h-px bg-border-landing-primary" />

      {/* Changelog Entries */}
      <div className="">
        <div className=" flex flex-col gap-8">
          {sortedEntries.map((entry) => {
            const config = categoryConfig[entry.category] || {
              color: "bg-landing-secondary border border-landing-border-primary text-landing-primary",
              icon: SparksSolid,
              label: "Unknown",
            };

            const IconComponent = config.icon;

            return (
              <Link
                key={entry.id}
                href={`/changelog/${entry.slug}`}
                className="block hover:shadow-md transition-shadow"
              >
                <article className="bg-white rounded-lg shadow-sm border border-landing-border-primary p-8">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}
                        >
                          <IconComponent className="w-4 h-4" />
                          {config.label}
                        </span>
                        <span className="text-sm text-landing-secondary font-mono">{entry.version}</span>
                      </div>

                      <h2 className="text-2xl font-bold text-landing-primary mb-2">{entry.title}</h2>

                      <p className="text-landing-secondary text-lg leading-relaxed mb-4">{entry.excerpt}</p>

                      <div className="flex items-center gap-4 text-sm text-landing-secondary">
                        <time dateTime={entry.createdAt}>{formatDate(entry.createdAt)}</time>
                        <span>•</span>
                        <span>{entry.readTime} min read</span>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  {entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-landing-border-primary">
                      {entry.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-3 py-1 bg-landing-secondary text-landing-primary text-sm rounded-full border border-landing-border-primary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              </Link>
            );
          })}
        </div>

        {/* Load More / Pagination could go here */}
        <div className="text-center mt-16">
          <p className="text-landing-secondary">That's all for now! Follow us for more updates.</p>
        </div>
      </div>
    </div>
  );
}
